import apiService from './api.service';
import type {
  TrailsData,
  Trail,
  TagWithCount,
  CourseSurveyStatus,
  CourseSurveyResponse,
  CourseSurveyPayload,
} from '@/types/trails';
import type {
  LessonContent,
  LessonCompleteResponse,
  TrailDetailAPI,
  LearnerLessonQuiz,
  QuizSubmitResult,
  LessonFSMStatus,
} from '@/types/trailDetail';

export interface LessonStateResponse {
  status: LessonFSMStatus;
  has_quiz: boolean;
  time_spent: number;
  video_percent: number;
  completed: boolean;
}

class TrailsService {
  private readonly baseUrl = '/trails';

  /**
   * Obtém tags públicas com contagem de cursos publicados
   */
  async getTags(): Promise<TagWithCount[]> {
    const response = await apiService.get<TagWithCount[]>('/tags/');
    return response.data;
  }

  /**
   * Obtém trilhas com filtros opcionais via query params
   */
  async getTrails(params?: { search?: string; tag?: string }): Promise<TrailsData> {
    const query = new URLSearchParams();
    if (params?.search) query.append('search', params.search);
    if (params?.tag && params.tag !== 'all') query.append('tag', params.tag);

    const queryStr = query.toString();
    const url = queryStr ? `${this.baseUrl}/?${queryStr}` : `${this.baseUrl}/`;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await apiService.get<any>(url);
    const raw = response.data;

    // A API pode retornar array direto ou paginado {results: [...]}
    const trailsArray = Array.isArray(raw) ? raw : (raw.results || []);

    // Adaptar os campos do backend pro formato do frontend
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const trails: Trail[] = trailsArray.map((t: any) => ({
      id: t.id,
      title: t.title,
      description: t.description,
      imageUrl: t.thumbnail || 'https://via.placeholder.com/400x200?text=Curso',
      duration: t.duration_minutes ? `${Math.round(t.duration_minutes / 60)}h totais` : '',
      level: this.mapLevel(t.level || t.level_display),
      category: t.category,
      status: this.mapStatus(t.status),
      progress: t.progress || 0,
      badge: t.status === 'in_progress' ? 'Em andamento' :
             t.status === 'completed' ? 'Concluído' : undefined,
      xpReward: t.xp_reward,
      xpEarned: t.xp_earned,
      lessonCount: t.lesson_count,
      lastLessonId: t.last_lesson_id,
      tags: t.tags || [],
      sector: t.sector || null,
    }));

    return {
      trails,
      userXP: 0, // Essa info vem do dashboard, não daqui
      completedCount: trails.filter(t => t.status === 'completed').length,
      activeCount: trails.filter(t => t.status === 'active').length,
    };
  }

  private mapLevel(level: string): 'Iniciante' | 'Intermediário' | 'Avançado' {
    const map: Record<string, 'Iniciante' | 'Intermediário' | 'Avançado'> = {
      'BEGINNER': 'Iniciante',
      'Beginner': 'Iniciante',
      'Iniciante': 'Iniciante',
      'INTERMEDIATE': 'Intermediário',
      'Intermediate': 'Intermediário',
      'Intermediário': 'Intermediário',
      'ADVANCED': 'Avançado',
      'Advanced': 'Avançado',
      'Avançado': 'Avançado',
    };
    return map[level] || 'Iniciante';
  }

  private mapStatus(status: string): 'active' | 'locked' | 'completed' {
    const map: Record<string, 'active' | 'locked' | 'completed'> = {
      'in_progress': 'active',
      'completed': 'completed',
      'not_started': 'active', // Trilhas não iniciadas são clicáveis
    };
    return map[status] || 'active';
  }

  /**
   * Obtém detalhes de uma trilha específica
   */
  async getTrailById(trailId: string): Promise<Trail> {
    const response = await apiService.get<Trail>(`${this.baseUrl}/${trailId}`);
    return response.data;
  }


  /**
   * Obtém detalhes de uma trilha (API real)
   */
  async getTrailDetail(trailId: string): Promise<TrailDetailAPI> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await apiService.get<any>(`${this.baseUrl}/${trailId}/`);
    const data = response.data;
    // Map nested instructor to flat fields
    const instructor = data.instructor || {};
    return {
      ...data,
      instructor_name: instructor.name || data.instructor_name || '',
      instructor_avatar: instructor.avatar || data.instructor_avatar || '',
      instructor_title: instructor.title || data.instructor_title || '',
    };
  }

  /**
   * Obtém conteúdo de uma lição específica
   */
  async getLessonContent(trailId: string, lessonId: string): Promise<LessonContent> {
    const response = await apiService.get<LessonContent>(
      `${this.baseUrl}/${trailId}/lessons/${lessonId}/`
    );
    return response.data;
  }

  /**
   * Marca uma lição como completa
   */
  async completeLesson(
    trailId: string,
    lessonId: string,
    payload: { time_spent: number; video_watched_percent?: number }
  ): Promise<LessonCompleteResponse> {
    const response = await apiService.put<LessonCompleteResponse>(
      `${this.baseUrl}/${trailId}/lessons/${lessonId}/complete/`,
      payload
    );
    return response.data;
  }
  /**
   * Obtém quiz de uma lição (alternativas randomizadas)
   */
  async getLessonQuiz(trailId: string, lessonId: string): Promise<LearnerLessonQuiz> {
    const response = await apiService.get<LearnerLessonQuiz>(
      `${this.baseUrl}/${trailId}/lessons/${lessonId}/quiz/`
    );
    return response.data;
  }

  /**
   * Submete respostas do quiz
   */
  async submitLessonQuiz(
    trailId: string,
    lessonId: string,
    answers: Record<string, string>
  ): Promise<QuizSubmitResult> {
    const response = await apiService.post<QuizSubmitResult>(
      `${this.baseUrl}/${trailId}/lessons/${lessonId}/quiz/submit/`,
      { answers }
    );
    return response.data;
  }

  /**
   * Obtém estado FSM atual da lição
   */
  async getLessonState(trailId: string, lessonId: string): Promise<LessonStateResponse> {
    const response = await apiService.get<LessonStateResponse>(
      `${this.baseUrl}/${trailId}/lessons/${lessonId}/state/`
    );
    return response.data;
  }

  /**
   * Estado da pesquisa de satisfação do curso para o usuário logado
   */
  async getCourseSurvey(trailId: string): Promise<CourseSurveyStatus> {
    const response = await apiService.get<CourseSurveyStatus>(
      `${this.baseUrl}/${trailId}/survey/`
    );
    return response.data;
  }

  /**
   * Envia resposta da pesquisa de satisfação do curso (CSAT + NPS).
   * Backend é idempotente: segundo POST retorna a resposta existente.
   */
  async submitCourseSurvey(
    trailId: string,
    payload: CourseSurveyPayload
  ): Promise<CourseSurveyResponse> {
    const response = await apiService.post<CourseSurveyResponse>(
      `${this.baseUrl}/${trailId}/survey/`,
      payload
    );
    return response.data;
  }
}

export const trailsService = new TrailsService();
